import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import { Scale, ArrowRight, ArrowLeft, Eye, EyeOff, Key, Building2, Check } from 'lucide-react';

const INPUT_CLS = "w-full bg-[#131C2E]/80 border border-white/8 rounded px-4 py-3 text-[#F0F4F8] placeholder-[#4A5E73] focus:outline-none focus:border-[#C07850]/50 transition-colors";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const isBusinessPlan = planParam === 'business';
  
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Auto-switch to register mode if coming from B2B CTA
  useEffect(() => {
    if (isBusinessPlan) {
      setIsLogin(false);
    }
  }, [isBusinessPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password, rememberMe);
      } else {
        if (!name.trim()) { setError('Navn er påkrevd'); setLoading(false); return; }
        if (!phone.trim()) { setError('Telefonnummer er påkrevd'); setLoading(false); return; }
        if (!street.trim() || !postalCode.trim() || !city.trim()) {
          setError('Fullstendig adresse er påkrevd'); setLoading(false); return;
        }
        const accountType = isBusinessPlan ? 'business' : 'private';
        await register(name, email, password, phone, { street, postal_code: postalCode, city }, accountType);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Noe gikk galt');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex auth-container" data-testid="auth-page">
      {/* Left - Form */}
      <div className="auth-form-section flex-1 flex items-center justify-center p-8 md:p-12 overflow-y-auto">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Tilbake-knapp */}
          <Link to="/" className="inline-flex items-center gap-2 text-[#8899A8] hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" />
            Tilbake til forsiden
          </Link>
          {/* Logo */}
          <div className="flex items-center gap-3 mb-2">
            <img src="https://customer-assets.emergentagent.com/job_juridisk-motor-no/artifacts/1x4dyzgp_image.jpg" alt="RettVerk" className="w-10 h-10 rounded object-contain logo" />
            <div>
              <h1 className="text-xl font-bold tracking-tight header-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
                RettVerk
              </h1>
              <p className="text-[10px] text-[#8899A8] tracking-widest uppercase">Norsk juridisk assistent</p>
            </div>
          </div>
          <p className="text-[#C07850] text-sm mb-8 italic">Klarhet i lovverket, kraft i dokumentene</p>

          <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isLogin ? 'Logg inn' : 'Opprett konto'}
          </h2>
          <p className="text-[#8899A8] mb-6">
            {isLogin ? 'Velkommen tilbake' : 'Få klarhet i lovverket og profesjonelle dokumenter — på dine premisser.'}
          </p>

          {/* B2B Registration Banner */}
          {isBusinessPlan && !isLogin && (
            <div className="bg-[#6366f1]/10 border border-[#6366f1]/30 rounded-lg p-4 mb-5" data-testid="b2b-registration-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#6366f1]/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-[#6366f1]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#6366f1]">Bedriftskonto</p>
                  <p className="text-xs text-[#8899A8]">Du registrerer en bedriftskonto med tilgang til B2B-funksjoner</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2.5 mb-5 text-red-400 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <>
                {/* Prøveperiode-info */}
                <div className="bg-[#22c55e]/10 border border-[#22c55e]/20 rounded-lg p-3 text-center">
                  <p className="text-xs text-[#22c55e] font-medium">30 dagers gratis prøveperiode — full tilgang til alle moduler</p>
                </div>
                <div>
                  <label className="block text-xs text-[#8899A8] mb-1.5 uppercase tracking-wider font-medium">Fullt navn</label>
                  <input data-testid="auth-name-input" type="text" value={name} onChange={e => setName(e.target.value)} className={INPUT_CLS} placeholder="Ola Nordmann" required />
                </div>
                <div>
                  <label className="block text-xs text-[#8899A8] mb-1.5 uppercase tracking-wider font-medium">Telefon</label>
                  <input data-testid="auth-phone-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={INPUT_CLS} placeholder="+47 000 00 000" required />
                </div>
              </>
            )}

            <div>
              <label className="block text-xs text-[#8899A8] mb-1.5 uppercase tracking-wider font-medium">E-post</label>
              <input data-testid="auth-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} className={INPUT_CLS} placeholder="din@epost.no" required />
            </div>
            <div>
              <label className="block text-xs text-[#8899A8] mb-1.5 uppercase tracking-wider font-medium">Passord</label>
              <div className="relative">
                <input data-testid="auth-password-input" type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className={`${INPUT_CLS} pr-12`} placeholder="Minimum 6 tegn" required minLength={6} />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8899A8] hover:text-[#F0F4F8] transition-colors" data-testid="toggle-password-visibility">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {isLogin && (
              <div className="flex items-center justify-between -mt-1">
                {/* Husk meg checkbox */}
                <label className="flex items-center gap-2 cursor-pointer group" data-testid="remember-me-label">
                  <div 
                    onClick={() => setRememberMe(!rememberMe)}
                    className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                      rememberMe 
                        ? 'bg-[#C07850]' 
                        : 'border border-[#4A5E73] group-hover:border-[#8899A8]'
                    }`}
                  >
                    {rememberMe && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="sr-only"
                    data-testid="remember-me-checkbox"
                  />
                  <span className="text-xs text-[#8899A8] group-hover:text-[#F0F4F8]">Husk meg</span>
                </label>
                
                <Link 
                  to="/forgot-password" 
                  className="text-[#8899A8] text-xs hover:text-[#C07850] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Glemt passord?
                </Link>
              </div>
            )}

            {!isLogin && (
              <div className="pt-2">
                <p className="text-xs text-[#C07850] uppercase tracking-wider mb-3 font-medium">Adresse</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-[#8899A8] mb-1.5 uppercase tracking-wider font-medium">Gate</label>
                    <input data-testid="auth-street-input" type="text" value={street} onChange={e => setStreet(e.target.value)} className={INPUT_CLS} placeholder="Storgata 12" required />
                  </div>
                  <div className="grid grid-cols-5 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs text-[#8899A8] mb-1.5 uppercase tracking-wider font-medium">Postnr.</label>
                      <input data-testid="auth-postal-code-input" type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)} className={INPUT_CLS} placeholder="7012" required maxLength={4} />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs text-[#8899A8] mb-1.5 uppercase tracking-wider font-medium">Poststed</label>
                      <input data-testid="auth-city-input" type="text" value={city} onChange={e => setCity(e.target.value)} className={INPUT_CLS} placeholder="Trondheim" required />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button data-testid="auth-submit-button" type="submit" disabled={loading}
              className="w-full bg-[#C07850] text-white font-semibold py-3 rounded uppercase tracking-wider text-sm hover:bg-[#A8654A] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 mt-2 min-h-[44px]">
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>{isLogin ? 'Logg inn' : 'Opprett konto'}<ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button data-testid="auth-toggle-mode" onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-[#8899A8] text-sm hover:text-[#C07850] transition-colors min-h-[44px] px-4">
              {isLogin ? 'Har du ikke konto? Registrer deg' : 'Har du allerede konto? Logg inn'}
            </button>
          </div>
        </div>
      </div>

      {/* Right - Visual (hidden on mobile in mobile-preview) */}
      <div className="auth-hero-section hidden lg:flex flex-1 relative overflow-hidden items-center justify-center bg-[#131C2E]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1120] via-[#131C2E] to-[#1B2740]" />
        <div className="relative z-10 p-16 max-w-lg">
          <div className="w-20 h-20 flex items-center justify-center mb-8">
            <img src="https://customer-assets.emergentagent.com/job_juridisk-motor-no/artifacts/1x4dyzgp_image.jpg" alt="RettVerk" className="w-20 h-20 object-contain" />
          </div>
          <h2 className="text-4xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Forstå loven.<br />Form dokumentet.
          </h2>
          <p className="text-[#8899A8] text-lg leading-relaxed">
            RettVerk gir klare forklaringer, profesjonelle dokumenter og full kontroll på rettighetene dine.
          </p>
          <div className="mt-8 flex gap-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#C07850]">1.2</div>
              <div className="text-[10px] text-[#8899A8] uppercase tracking-wider mt-0.5">Versjon</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#C07850]">2026</div>
              <div className="text-[10px] text-[#8899A8] uppercase tracking-wider mt-0.5">Oppdatert</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#C07850]">AI</div>
              <div className="text-[10px] text-[#8899A8] uppercase tracking-wider mt-0.5">Drevet</div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-white/5">
            <p className="text-xs text-[#8899A8]/80 mb-2">
              Sist oppdatert: 20. februar 2026 — versjon 1.2.0
            </p>
            <p className="text-xs text-[#8899A8]/60">
              Utviklet av Lars Strømberg — RettVerk
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
